import { useMemo } from "react";
import { Box, Chip } from "@mui/material";
import type { RagAssistantMessage } from "../../types/ragChatTypes";
import { badgeForQueryType } from "../../constants/ragUiConstants";
import { mergePageAlerts, splitAnswerContent } from "../../utils/answerContentUtils";
import { alternativePdfsNotInSources } from "../../utils/ragChatUtils";
import { RagAnswerText } from "./RagAnswerText";
import {
  RagAlertsSection,
  RagAlternativePdfs,
  RagAlternativeSources,
  RagBestAnswerHeader,
  RagImageRail,
  RagOptionButtons,
  RagRelatedQuestions,
  RagSourcesList,
  RagTablesSection,
  RagVideosList,
} from "./RagChatBlocks";

interface RagAssistantMessageCardProps {
  msg: RagAssistantMessage;
  selectedOption: string | null;
  loading: boolean;
  onOptionSelect: (msgId: string, option: string) => void;
  onRelatedSelect: (question: string) => void;
  onAlternativePdf: (pdf: string) => void;
}

export function RagAssistantMessageCard({
  msg,
  selectedOption,
  loading,
  onOptionSelect,
  onRelatedSelect,
  onAlternativePdf,
}: RagAssistantMessageCardProps) {
  const badge = msg.queryType ? badgeForQueryType(msg.queryType) : null;
  const isAnswer = msg.responseType === "answer";

  const { answerBody, displayAlerts, trailingCallout } = useMemo(() => {
    if (!isAnswer) {
      return {
        answerBody: msg.answer,
        displayAlerts: msg.pageAlerts ?? {},
        trailingCallout: "",
      };
    }
    const split = splitAnswerContent(msg.answer);
    return {
      answerBody: split.body,
      displayAlerts: mergePageAlerts(msg.pageAlerts ?? {}, split.extractedAlerts),
      trailingCallout: split.trailingCallout,
    };
  }, [isAnswer, msg.answer, msg.pageAlerts]);

  const hasSafetyAlerts =
    isAnswer &&
    Object.values(displayAlerts).some((alerts) => (alerts?.length ?? 0) > 0);

  const hasAltSources = isAnswer && (msg.alternativeSources?.length ?? 0) > 0;
  const showBestAnswerHeader = hasAltSources && !!msg.primarySource;

  const alternativePdfsToShow = useMemo(
    () => alternativePdfsNotInSources(msg.alternativePdfs, msg.alternativeSources),
    [msg.alternativePdfs, msg.alternativeSources]
  );
  const showAlternativePdfs = isAnswer && alternativePdfsToShow.length > 0;

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
      <Box
        className="rag-assistant-bubble"
        sx={{
          width: "100%",
          maxWidth: "100%",
          borderRadius: "16px 16px 16px 4px",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "grey.50",
          p: 2,
          boxShadow: "0 2px 8px rgba(28,46,54,0.06)",
        }}
      >
        {badge && (
          <Chip
            size="small"
            label={badge.label}
            color={badge.chipColor}
            sx={{ mb: 1.5, fontWeight: 600 }}
          />
        )}

        {isAnswer && (
          <RagAlertsSection pageAlerts={displayAlerts} placement="top" defaultOpen={false} />
        )}

        {showBestAnswerHeader && msg.primarySource && (
          <RagBestAnswerHeader primary={msg.primarySource} />
        )}

        <Box sx={{ mt: hasSafetyAlerts ? 1.5 : 0 }}>
          <RagAnswerText
            text={answerBody}
            inlineImages={isAnswer ? msg.inlineImages ?? {} : {}}
            showRichMedia={isAnswer}
          />
          {isAnswer && (
            <RagImageRail
              images={msg.images}
              inlineImages={msg.inlineImages ?? {}}
              msgId={msg.id}
              answerText={answerBody}
            />
          )}
        </Box>

        {isAnswer && trailingCallout && (
          <Box sx={{ mt: 2 }}>
            <RagAnswerText text={trailingCallout} inlineImages={{}} showRichMedia={false} />
          </Box>
        )}

        {msg.options?.length > 0 && (
          <RagOptionButtons
            options={msg.options}
            selectedOption={selectedOption}
            disabled={loading}
            onSelect={(opt) => onOptionSelect(msg.id, opt)}
          />
        )}

        {isAnswer && <RagVideosList videos={msg.videos} msgId={msg.id} />}
        {isAnswer && <RagTablesSection pageTables={msg.pageTables} defaultOpen={false} />}
        {showAlternativePdfs && (
          <RagAlternativePdfs
            pdfs={alternativePdfsToShow}
            onSelect={onAlternativePdf}
            defaultOpen={false}
          />
        )}
        {hasAltSources && (
          <RagAlternativeSources
            sources={msg.alternativeSources}
            onShowDetails={onAlternativePdf}
            defaultOpen
          />
        )}

        <RagSourcesList sources={msg.sources} msgId={msg.id} defaultOpen />
        {isAnswer && (
          <RagRelatedQuestions
            questions={msg.relatedQuestions}
            onSelect={onRelatedSelect}
            maxVisible={2}
          />
        )}
      </Box>
    </Box>
  );
}
